import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Settings, Shield, Bell, CreditCard, ClipboardList, Save, Plus, Trash2, Users,
  Package, BarChart3, Edit2, Archive, Eye, EyeOff, Star, Mail, Copy, X,
  Upload, ImageIcon
} from 'lucide-react';
import { SUPPORTED_LOCALES, useLocale } from '../contexts/LocaleContext';

export default function AdminPage({ settingsMode = false }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const templateScope = !settingsMode && user?.role === 'super_admin' ? 'global' : 'account';
  const showSubscriptionTab = true;

  const title = settingsMode ? 'Settings' : 'Admin';
  const subtitle = settingsMode
    ? 'Manage your account settings and configuration'
    : 'Manage your account settings and commercial configuration';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid={settingsMode ? "settings-title" : "admin-title"}>{title}</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="company" data-testid="admin-tab-company"><Settings className="w-4 h-4 mr-2" />Company</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>
          {!settingsMode && <TabsTrigger value="plans" data-testid="admin-tab-plans"><Package className="w-4 h-4 mr-2" />Plans</TabsTrigger>}
          <TabsTrigger value="email_templates" data-testid="admin-tab-email-templates"><Mail className="w-4 h-4 mr-2" />Email Templates</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="admin-tab-reminders"><Bell className="w-4 h-4 mr-2" />Reminders</TabsTrigger>
          {showSubscriptionTab && (
            <TabsTrigger value="subscription" data-testid="admin-tab-subscription">
              <CreditCard className="w-4 h-4 mr-2" />
              {settingsMode ? 'Subscription' : 'Subscriber'}
            </TabsTrigger>
          )}
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ClipboardList className="w-4 h-4 mr-2" />Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4"><CompanySettings /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
        {!settingsMode && <TabsContent value="plans" className="mt-4"><PlansPanel /></TabsContent>}
        <TabsContent value="email_templates" className="mt-4"><EmailTemplatesPanel scope={templateScope} /></TabsContent>
        <TabsContent value="reminders" className="mt-4"><RemindersPanel /></TabsContent>
        {showSubscriptionTab && (
          <TabsContent value="subscription" className="mt-4">
            {!settingsMode ? (
              <SuperAdminSubscriptionPanel />
            ) : (
              <SubscriptionPanel />
            )}
          </TabsContent>
        )}
        <TabsContent value="audit" className="mt-4"><AuditLogPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Plans Panel (Commercial Admin) ───
function PlansPanel() {
  const [plans, setPlans] = useState([]);
  const [overview, setOverview] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlans = useCallback(() => {
    Promise.all([
      api.get('/admin/plans?include_archived=true'),
      api.get('/admin/plans/subscriptions/overview').catch(() => ({ data: null })),
    ]).then(([p, o]) => {
      setPlans(p.data);
      setOverview(o.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleArchive = async (planId) => {
    await api.post(`/admin/plans/${planId}/archive`);
    toast.success('Plan archived');
    fetchPlans();
  };

  const handleUnarchive = async (planId) => {
    await api.post(`/admin/plans/${planId}/unarchive`);
    toast.success('Plan restored');
    fetchPlans();
  };

  if (loading) return <div className="p-4 text-slate-500">Loading plans...</div>;

  return (
    <div className="space-y-6">
      {/* Subscription Overview */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Subscribers</p>
              <p className="text-2xl font-bold text-slate-900">{overview.total_subscribers}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{overview.active_count}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Cancelled</p>
              <p className="text-2xl font-bold text-slate-500">{overview.cancelled_count}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Past Due</p>
              <p className="text-2xl font-bold text-rose-600">{overview.past_due_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan breakdown */}
      {overview?.plan_breakdown?.length > 0 && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Plan Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {overview.plan_breakdown.map(b => (
                <div key={b.plan_id || 'legacy'} className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">{b.plan_name}:</span>
                  <Badge variant="outline">{b.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Subscription Plans</h2>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingPlan(null); setShowCreate(true); }} data-testid="add-plan-button">
          <Plus className="w-4 h-4 mr-2" /> Create Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(plan => (
          <Card key={plan.id} className={`border shadow-sm ${plan.archived_at ? 'opacity-60 border-slate-300' : plan.is_recommended ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                    {plan.is_recommended && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                  </div>
                  <p className="text-xs text-slate-500">{plan.slug} &middot; Order: {plan.display_order}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-blue-600">${parseFloat(plan.monthly_price).toFixed(0)}<span className="text-sm text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-400">{plan.subscriber_count} subscribers</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-3">{plan.description}</p>

              <div className="flex flex-wrap gap-1 mb-3">
                {plan.is_active && <Badge className="text-xs bg-emerald-100 text-emerald-700">Active</Badge>}
                {!plan.is_active && <Badge className="text-xs bg-slate-100 text-slate-500">Inactive</Badge>}
                {plan.is_public ? <Badge className="text-xs bg-blue-100 text-blue-700"><Eye className="w-3 h-3 mr-1" />Public</Badge> : <Badge className="text-xs bg-slate-100 text-slate-500"><EyeOff className="w-3 h-3 mr-1" />Hidden</Badge>}
                {plan.archived_at && <Badge className="text-xs bg-amber-100 text-amber-700">Archived</Badge>}
              </div>

              {/* Features summary */}
              {plan.features.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-slate-600 mb-1">Features/Limits:</p>
                  <div className="space-y-0.5">
                    {plan.features.map(f => (
                      <div key={f.id} className="text-xs flex justify-between">
                        <span className="text-slate-600">{f.feature_key}</span>
                        <span className="font-medium text-slate-800">{f.is_unlimited ? 'Unlimited' : f.feature_value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setEditingPlan(plan); setShowCreate(true); }} data-testid={`edit-plan-${plan.slug}`}>
                  <Edit2 className="w-3 h-3 mr-1" /> Edit
                </Button>
                {!plan.archived_at ? (
                  <Button size="sm" variant="outline" className="text-amber-600" onClick={() => handleArchive(plan.id)}>
                    <Archive className="w-3 h-3 mr-1" /> Archive
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleUnarchive(plan.id)}>
                    Restore
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PlanDialog
        open={showCreate}
        plan={editingPlan}
        onClose={() => { setShowCreate(false); setEditingPlan(null); }}
        onDone={() => { setShowCreate(false); setEditingPlan(null); fetchPlans(); }}
      />
    </div>
  );
}

// ─── Plan Create/Edit Dialog ───
const FEATURE_KEYS = [
  { key: 'max_clients', label: 'Max Clients', type: 'limit' },
  { key: 'max_projects', label: 'Max Projects', type: 'limit' },
  { key: 'max_invoices_per_month', label: 'Max Invoices/Month', type: 'limit' },
  { key: 'max_users', label: 'Max Users', type: 'limit' },
  { key: 'multi_user', label: 'Multi-User', type: 'toggle' },
  { key: 'reminders', label: 'Reminders', type: 'toggle' },
  { key: 'partial_invoicing', label: 'Partial Invoicing', type: 'toggle' },
  { key: 'reports', label: 'Reports', type: 'toggle' },
  { key: 'accountant_handover', label: 'Accountant Handover', type: 'toggle' },
];

function PlanDialog({ open, plan, onClose, onDone }) {
  const isEdit = !!plan;
  const [form, setForm] = useState({
    name: '', slug: '', description: '', monthly_price: '0', yearly_price: '',
    stripe_product_id: '', stripe_price_id: '', display_order: '0', is_active: true,
    is_public: true, is_recommended: false, cta_text: 'Get Started', bullet_points_text: '',
  });
  const [features, setFeatures] = useState(
    FEATURE_KEYS.map(fk => ({ feature_key: fk.key, feature_value: fk.type === 'toggle' ? 'false' : '0', is_unlimited: false }))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setForm({
        name: plan.name, slug: plan.slug, description: plan.description,
        monthly_price: String(plan.monthly_price), yearly_price: plan.yearly_price ? String(plan.yearly_price) : '',
        stripe_product_id: plan.stripe_product_id || '', stripe_price_id: plan.stripe_price_id || '',
        display_order: String(plan.display_order),
        is_active: plan.is_active, is_public: plan.is_public, is_recommended: plan.is_recommended,
        cta_text: plan.cta_text, bullet_points_text: (plan.bullet_points || []).join('\n'),
      });
      const existingFeatures = FEATURE_KEYS.map(fk => {
        const existing = plan.features.find(f => f.feature_key === fk.key);
        return existing
          ? { feature_key: fk.key, feature_value: existing.feature_value, is_unlimited: existing.is_unlimited }
          : { feature_key: fk.key, feature_value: fk.type === 'toggle' ? 'false' : '0', is_unlimited: false };
      });
      setFeatures(existingFeatures);
    } else {
      setForm({ name: '', slug: '', description: '', monthly_price: '0', yearly_price: '', stripe_product_id: '', stripe_price_id: '', display_order: '0', is_active: true, is_public: true, is_recommended: false, cta_text: 'Get Started', bullet_points_text: '' });
      setFeatures(FEATURE_KEYS.map(fk => ({ feature_key: fk.key, feature_value: fk.type === 'toggle' ? 'false' : '0', is_unlimited: false })));
    }
  }, [plan, open]);

  const updateFeature = (key, field, value) => {
    setFeatures(prevFeatures => prevFeatures.map(f => 
      f.feature_key === key ? { ...f, [field]: value } : f
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.error('Name and slug required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, slug: form.slug, description: form.description,
        monthly_price: parseFloat(form.monthly_price) || 0,
        yearly_price: form.yearly_price ? parseFloat(form.yearly_price) : null,
        display_order: parseInt(form.display_order) || 0,
        is_active: form.is_active, is_public: form.is_public,
        is_recommended: form.is_recommended, cta_text: form.cta_text,
        bullet_points: form.bullet_points_text.split('\n').filter(l => l.trim()),
        features: features,
      };

      if (isEdit) {
        await api.put(`/admin/plans/${plan.id}`, payload);
        toast.success('Plan updated');
      } else {
        await api.post('/admin/plans', payload);
        toast.success('Plan created');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Plan' : 'Create Plan'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Plan Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Pro" data-testid="plan-name" />
            </div>
            <div className="space-y-2">
              <Label>Slug * (unique identifier)</Label>
              <Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="pro" disabled={isEdit} data-testid="plan-slug" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Monthly Price ($)</Label>
              <Input type="number" step="0.01" value={form.monthly_price} onChange={e => setForm({...form, monthly_price: e.target.value})} data-testid="plan-monthly-price" />
            </div>
            <div className="space-y-2">
              <Label>Yearly Price ($) <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input type="number" step="0.01" value={form.yearly_price} onChange={e => setForm({...form, yearly_price: e.target.value})} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input type="number" value={form.display_order} onChange={e => setForm({...form, display_order: e.target.value})} />
            </div>
          </div>

          {isEdit && (form.stripe_product_id || form.stripe_price_id) && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1">
              <p className="text-xs font-medium text-slate-700">Stripe Sync Status</p>
              {form.stripe_product_id && <p className="text-xs text-slate-500">Product: <code className="bg-white px-1 py-0.5 rounded text-slate-700">{form.stripe_product_id}</code></p>}
              {form.stripe_price_id && <p className="text-xs text-slate-500">Price: <code className="bg-white px-1 py-0.5 rounded text-slate-700">{form.stripe_price_id}</code></p>}
              <p className="text-xs text-slate-400">Auto-managed. Stripe product and price are synced when you save.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CTA Button Text</Label>
              <Input value={form.cta_text} onChange={e => setForm({...form, cta_text: e.target.value})} />
            </div>
            <div className="flex items-end gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
                <Label className="text-sm">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_public} onCheckedChange={v => setForm({...form, is_public: v})} />
                <Label className="text-sm">Public</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_recommended} onCheckedChange={v => setForm({...form, is_recommended: v})} />
                <Label className="text-sm">Recommended</Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Pricing Page Bullet Points (one per line)</Label>
            <Textarea value={form.bullet_points_text} onChange={e => setForm({...form, bullet_points_text: e.target.value})} rows={4} placeholder="Unlimited clients\nUnlimited invoices\nFull reports" />
          </div>

          {/* Feature/Limit Configuration */}
          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Feature & Limit Configuration</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">Feature</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead className="w-28">Unlimited</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {features.map(f => {
                    const def = FEATURE_KEYS.find(fk => fk.key === f.feature_key);
                    return (
                      <TableRow key={f.feature_key}>
                        <TableCell className="text-sm font-medium">{def?.label || f.feature_key}</TableCell>
                        <TableCell>
                          {def?.type === 'toggle' ? (
                            <Select value={f.is_unlimited ? 'unlimited' : f.feature_value} onValueChange={v => {
                              if (v === 'unlimited') {
                                updateFeature(f.feature_key, 'is_unlimited', true);
                              } else {
                                updateFeature(f.feature_key, 'is_unlimited', false);
                                updateFeature(f.feature_key, 'feature_value', v);
                              }
                            }}>
                              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Yes</SelectItem>
                                <SelectItem value="false">No</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              className="w-24"
                              type="number"
                              value={f.is_unlimited ? '' : f.feature_value}
                              onChange={e => {
                                const newValue = e.target.value;
                                setFeatures(prevFeatures => prevFeatures.map(feat => 
                                  feat.feature_key === f.feature_key 
                                    ? { ...feat, feature_value: newValue, is_unlimited: false }
                                    : feat
                                ));
                              }}
                              disabled={f.is_unlimited}
                              placeholder={f.is_unlimited ? '∞' : '0'}
                              data-testid={`feature-${f.feature_key}-input`}
                              min="0"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {def?.type === 'limit' && (
                            <Switch
                              checked={f.is_unlimited}
                              onCheckedChange={v => updateFeature(f.feature_key, 'is_unlimited', v)}
                              data-testid={`feature-${f.feature_key}-unlimited`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-plan-submit">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Logo Upload Section ───
function LogoUploadSection({ logoUrl, onLogoChange }) {
  const [uploading, setUploading] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [showUrlField, setShowUrlField] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PNG, JPG, and SVG files are accepted.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2 MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/account/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onLogoChange(res.data.company_logo_url);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setUploading(true);
    try {
      const res = await api.delete('/account/logo');
      onLogoChange(res.data.company_logo_url || '');
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove logo');
    } finally {
      setUploading(false);
    }
  };

  const handleManualUrlSave = () => {
    onLogoChange(manualUrl);
    setShowUrlField(false);
    toast.success('Logo URL updated. Click "Save Settings" to persist.');
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-slate-900">Company Logo</Label>
      <p className="text-xs text-slate-500">Your logo appears in customer emails and branded documents. Max 2 MB. PNG, JPG, or SVG.</p>

      {logoUrl ? (
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex-shrink-0 w-48 h-16 bg-white rounded border border-slate-200 flex items-center justify-center overflow-hidden p-2">
            <img
              src={logoUrl}
              alt="Company logo"
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="hidden items-center justify-center text-xs text-slate-400">Failed to load</div>
          </div>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <p className="text-xs text-slate-500 truncate" title={logoUrl}>{logoUrl}</p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-3 h-3 mr-1" /> Replace
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                disabled={uploading}
                onClick={handleRemoveLogo}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon className="w-10 h-10 text-slate-400 mb-2" />
          <p className="text-sm font-medium text-slate-600">
            {uploading ? 'Uploading...' : 'Click to upload your logo'}
          </p>
          <p className="text-xs text-slate-400 mt-1">PNG, JPG, or SVG up to 2 MB</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        className="hidden"
        onChange={handleFileUpload}
      />

      {!showUrlField ? (
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline"
          onClick={() => { setManualUrl(logoUrl || ''); setShowUrlField(true); }}
        >
          Or enter a logo URL manually
        </button>
      ) : (
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Logo URL</Label>
            <Input
              value={manualUrl}
              onChange={e => setManualUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="text-sm"
            />
          </div>
          <Button type="button" size="sm" onClick={handleManualUrlSave}>Apply</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setShowUrlField(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

// ─── Company Settings ───
function CompanySettings() {
  const { setLocale } = useLocale();
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/account').then(r => {
      setAccount(r.data);
      setForm(r.data);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load account settings:', err);
      setError('Failed to load account settings. Please refresh the page.');
      setLoading(false);
      // Set safe defaults
      setAccount({});
      setForm({
        company_name: '',
        company_address: '',
        company_email: '',
        company_phone: '',
        company_logo_url: '',
        gst_enabled: false,
        pst_enabled: false,
        gst_rate: 5,
        pst_rate: 7,
        operating_region: 'CA',
        default_locale: 'en-CA',
        default_document_locale: 'en-CA',
        base_currency: 'CAD',
        timezone: 'America/Vancouver',
        tax_jurisdiction: 'CA-BC',
        default_payment_terms: 'Net 30',
        invoice_prefix: 'INV-',
        quote_prefix: 'QT-',
        estimate_prefix: 'EST-'
      });
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/account', {
        company_name: form.company_name, company_address: form.company_address,
        company_email: form.company_email, company_phone: form.company_phone,
        company_logo_url: form.company_logo_url,
        gst_enabled: form.gst_enabled, gst_number: form.gst_number, gst_rate: form.gst_rate,
        pst_enabled: form.pst_enabled, pst_rate: form.pst_rate,
        default_payment_terms: form.default_payment_terms,
        invoice_prefix: form.invoice_prefix, quote_prefix: form.quote_prefix, estimate_prefix: form.estimate_prefix,
        operating_region: form.operating_region,
        default_locale: form.default_locale,
        default_document_locale: form.default_document_locale,
        base_currency: form.base_currency,
        timezone: form.timezone,
        tax_jurisdiction: form.tax_jurisdiction,
      });
      setAccount(res.data);
      setLocale(res.data.default_locale || 'en-CA');
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (!account) return <div className="p-4 text-slate-500">Loading...</div>;
  const handleChange = (field, value) => setForm({ ...form, [field]: value });
  const taxUi = form.operating_region === 'DE_EU'
    ? {
        title: 'Current document tax / Aktuelle Dokumentsteuer',
        primary: 'VAT / Umsatzsteuer', primaryHelp: 'Enable if the company must charge German VAT.',
        number: 'VAT ID or tax number / USt-IdNr. oder Steuernummer', rate: 'VAT rate / Umsatzsteuersatz',
        showSecondary: false,
      }
    : form.operating_region === 'US'
      ? {
          title: 'Current document tax', primary: 'Sales tax', primaryHelp: 'Enable only when sales tax applies to your services and jurisdiction.',
          number: 'Tax registration number', rate: 'Sales-tax rate', showSecondary: false,
        }
      : {
          title: 'Current document taxes', primary: 'GST (Federal)', primaryHelp: 'Enable if the company is GST-registered.',
          number: 'GST Number', rate: 'GST Rate', showSecondary: true,
        };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-slate-500">Loading company settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-red-900">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">ServiceFlow Edition</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business location</Label>
              <Select value={form.operating_region || 'CA'} onValueChange={value => {
                const defaults = {
                  CA: { default_locale: 'en-CA', default_document_locale: 'en-CA', base_currency: 'CAD', timezone: 'America/Vancouver', tax_jurisdiction: 'CA-BC' },
                  US: { default_locale: 'en-US', default_document_locale: 'en-US', base_currency: 'USD', timezone: 'America/New_York', tax_jurisdiction: 'US' },
                  DE_EU: { default_locale: 'de-DE', default_document_locale: 'de-DE', base_currency: 'EUR', timezone: 'Europe/Berlin', tax_jurisdiction: 'DE' },
                };
                setForm({ ...form, operating_region: value, ...defaults[value] });
              }}>
                <SelectTrigger data-testid="admin-operating-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CA">Canada · English · CAD</SelectItem>
                  <SelectItem value="US">United States · English · USD</SelectItem>
                  <SelectItem value="DE_EU">Germany / Europe · Deutsch · EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Application and help language</Label>
              <Select value={form.default_locale || 'en-CA'} onValueChange={value => handleChange('default_locale', value)}>
                <SelectTrigger data-testid="admin-application-language"><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPORTED_LOCALES.map(item => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Customer-document language</Label>
              <Select value={form.default_document_locale || 'en-CA'} onValueChange={value => handleChange('default_document_locale', value)}>
                <SelectTrigger data-testid="admin-document-language"><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPORTED_LOCALES.map(item => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Base currency</Label><Input value={form.base_currency || ''} onChange={e => handleChange('base_currency', e.target.value.toUpperCase())} /></div>
            <div className="space-y-2"><Label>Timezone</Label><Input value={form.timezone || ''} onChange={e => handleChange('timezone', e.target.value)} /></div>
            <div className="space-y-2"><Label>Tax jurisdiction</Label><Input value={form.tax_jurisdiction || ''} onChange={e => handleChange('tax_jurisdiction', e.target.value)} /></div>
          </div>
          <p className="text-xs text-slate-500">Region and languages remain editable for demonstrations. Changes affect future defaults only; existing quotes, invoices, payments, and frozen tax totals are preserved.</p>
        </CardContent>
      </Card>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-lg font-medium">Company Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Company Name</Label><Input value={form.company_name || ''} onChange={e => handleChange('company_name', e.target.value)} data-testid="admin-company-name" /></div>
            <div className="space-y-2"><Label>Company Email</Label><Input value={form.company_email || ''} onChange={e => handleChange('company_email', e.target.value)} /></div>
            <div className="space-y-2"><Label>Company Phone</Label><Input value={form.company_phone || ''} onChange={e => handleChange('company_phone', e.target.value)} /></div>
            <div className="space-y-2"><Label>Payment Terms</Label><Input value={form.default_payment_terms || ''} onChange={e => handleChange('default_payment_terms', e.target.value)} /></div>
          </div>
          <div className="space-y-2"><Label>Company Address</Label><Input value={form.company_address || ''} onChange={e => handleChange('company_address', e.target.value)} /></div>
          <LogoUploadSection logoUrl={form.company_logo_url} onLogoChange={(url) => { handleChange('company_logo_url', url); setAccount(prev => ({ ...prev, company_logo_url: url })); }} />
        </CardContent>
      </Card>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">{taxUi.title}</CardTitle>
          <p className="text-xs text-slate-500">These switches and rates drive calculations on new documents. Each saved document keeps its original tax amounts and rate snapshots.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div><p className="text-sm font-medium text-slate-900">{taxUi.primary}</p><p className="text-xs text-slate-500">{taxUi.primaryHelp}</p></div>
            <Switch checked={form.gst_enabled || false} onCheckedChange={v => handleChange('gst_enabled', v)} data-testid="admin-gst-toggle" />
          </div>
          {form.gst_enabled && (
            <div className="grid grid-cols-2 gap-4 pl-4">
              <div className="space-y-2"><Label>{taxUi.number}</Label><Input value={form.gst_number || ''} onChange={e => handleChange('gst_number', e.target.value)} data-testid="admin-gst-number" /></div>
              <div className="space-y-2"><Label>{taxUi.rate} (%)</Label><Input type="number" min="0" max="100" step="0.0001" value={form.gst_rate ?? ''} onChange={e => handleChange('gst_rate', e.target.value)} /></div>
            </div>
          )}
          {taxUi.showSecondary && (
            <>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div><p className="text-sm font-medium text-slate-900">PST (BC Provincial)</p><p className="text-xs text-slate-500">Enable only when BC PST applies.</p></div>
                <Switch checked={form.pst_enabled || false} onCheckedChange={v => handleChange('pst_enabled', v)} data-testid="admin-pst-toggle" />
              </div>
              {form.pst_enabled && (
                <div className="pl-4"><div className="space-y-2 max-w-xs"><Label>PST Rate (%)</Label><Input type="number" min="0" max="100" step="0.0001" value={form.pst_rate ?? ''} onChange={e => handleChange('pst_rate', e.target.value)} /></div></div>
              )}
            </>
          )}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Taxability can vary by service and customer. Confirm registration, exemptions, and cross-border treatment with a qualified tax adviser. The special German B2B-to-non-EU treatment is selected on the quote or invoice, not here.
          </div>
        </CardContent>
      </Card>

      <TaxRulesPanel jurisdiction={form.tax_jurisdiction || 'CA-BC'} />

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-lg font-medium">Document Prefixes</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Invoice Prefix</Label><Input value={form.invoice_prefix || ''} onChange={e => handleChange('invoice_prefix', e.target.value)} /></div>
            <div className="space-y-2"><Label>Quote Prefix</Label><Input value={form.quote_prefix || ''} onChange={e => handleChange('quote_prefix', e.target.value)} /></div>
            <div className="space-y-2"><Label>Estimate Prefix</Label><Input value={form.estimate_prefix || ''} onChange={e => handleChange('estimate_prefix', e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving} data-testid="admin-save-settings">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}

function TaxRulesPanel({ jurisdiction }) {
  const [rules, setRules] = useState([]);
  const [adding, setAdding] = useState(false);
  const [replacingRule, setReplacingRule] = useState(null);
  const [form, setForm] = useState({
    tax_code: '', name: '', invoice_label: '', rate: '',
    valid_from: new Date().toISOString().slice(0, 10), valid_to: '',
  });

  const loadRules = useCallback(() => {
    api.get('/tax-rules').then(r => setRules(r.data)).catch(() => setRules([]));
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  const addRule = async () => {
    if (!form.tax_code || !form.name || !form.invoice_label || form.rate === '') {
      toast.error('Tax code, name, invoice label and rate are required');
      return;
    }
    setAdding(true);
    try {
      const payload = {
        ...form,
        jurisdiction,
        rate: Number(form.rate),
        valid_to: form.valid_to || null,
      };
      if (replacingRule) {
        await api.post(`/tax-rules/${replacingRule.id}/replace`, payload);
        toast.success('New dated tax rate scheduled');
      } else {
        await api.post('/tax-rules', payload);
        toast.success('Tax rule added to history');
      }
      setForm({ tax_code: '', name: '', invoice_label: '', rate: '', valid_from: new Date().toISOString().slice(0, 10), valid_to: '' });
      setReplacingRule(null);
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tax rule');
    } finally {
      setAdding(false);
    }
  };

  const beginReplacement = (rule) => {
    setReplacingRule(rule);
    setForm({
      tax_code: rule.tax_code,
      name: rule.name,
      invoice_label: rule.invoice_label,
      rate: '',
      valid_from: new Date().toISOString().slice(0, 10),
      valid_to: '',
    });
  };

  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-medium">Tax-rate history and scheduled changes</CardTitle>
        <p className="text-xs text-slate-500">This dated register documents when rates change. The current calculation switches and rates above remain the source used for new documents.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rules.length > 0 && (
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Invoice label</TableHead><TableHead>Rate</TableHead><TableHead>Valid from</TableHead><TableHead>Valid to</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>{rules.map(rule => (
              <TableRow key={rule.id}>
                <TableCell className="font-medium">{rule.tax_code}</TableCell>
                <TableCell>{rule.invoice_label}</TableCell>
                <TableCell>{rule.rate}%</TableCell>
                <TableCell>{rule.valid_from}</TableCell>
                <TableCell>{rule.valid_to || 'Current'}</TableCell>
                <TableCell className="text-right">
                  {!rule.valid_to && <Button size="sm" variant="ghost" onClick={() => beginReplacement(rule)}>Schedule replacement</Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
        {replacingRule && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            Scheduling a new version of <strong>{replacingRule.tax_code}</strong>. The old version will end the day before the new start date.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 rounded-lg bg-slate-50 p-4">
          <div className="space-y-1"><Label>Code</Label><Input disabled={Boolean(replacingRule)} value={form.tax_code} onChange={e => setForm({ ...form, tax_code: e.target.value.toUpperCase() })} placeholder="GST" /></div>
          <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Federal tax" /></div>
          <div className="space-y-1"><Label>Invoice label</Label><Input value={form.invoice_label} onChange={e => setForm({ ...form, invoice_label: e.target.value })} placeholder="GST" /></div>
          <div className="space-y-1"><Label>Rate %</Label><Input type="number" min="0" max="100" step="0.0001" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} /></div>
          <div className="space-y-1"><Label>Valid from</Label><Input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} /></div>
          <div className="space-y-1"><Label>Valid to</Label><Input type="date" value={form.valid_to} onChange={e => setForm({ ...form, valid_to: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2">
          {replacingRule && <Button variant="ghost" onClick={() => { setReplacingRule(null); setForm({ tax_code: '', name: '', invoice_label: '', rate: '', valid_from: new Date().toISOString().slice(0, 10), valid_to: '' }); }}>Cancel</Button>}
          <Button variant="outline" onClick={addRule} disabled={adding}><Plus className="w-4 h-4 mr-2" />{adding ? 'Saving...' : replacingRule ? 'Schedule new rate' : 'Add history entry'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Users Panel ───
function UsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'staff',
  });

  const canManageUsers = user && ['account_owner', 'admin'].includes(user.role);

  const fetchUsers = useCallback(() => {
    api.get('/users').then(r => setUsers(r.data)).catch(err => {
      console.error('Failed to load users:', err);
      setUsers([]);
    });
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!canManageUsers) return;
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.error('All user fields are required');
      return;
    }
    setCreating(true);
    try {
      await api.post('/users', form);
      toast.success('Team member added');
      setForm({ first_name: '', last_name: '', email: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader><CardTitle className="text-lg font-medium">Team Members</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {canManageUsers && (
          <form onSubmit={handleCreateUser} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Last Name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={creating}>
                {creating ? 'Adding...' : 'Add Team Member'}
              </Button>
            </div>
          </form>
        )}

        <div className="border border-slate-200 rounded-lg overflow-hidden">
        <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
          <TableBody>{users.map(u => (<TableRow key={u.id}><TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell><TableCell>{u.email}</TableCell><TableCell><Badge variant="outline" className="text-xs">{u.role}</Badge></TableCell><TableCell><Badge className={`text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{u.is_active ? 'Active' : 'Inactive'}</Badge></TableCell><TableCell className="text-sm">{formatDate(u.created_at)}</TableCell></TableRow>))}</TableBody>
        </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Reminders Panel ───
function RemindersPanel() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    reminders_enabled: true,
    payment_reminder_enabled: true,
    overdue_reminder_enabled: true,
    payment_reminder_days_before_due: 3,
    overdue_reminder_days_after_due: 7,
    sender_name_override: '',
    reply_to_override: '',
    secure_links_enabled: true,
    is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/reminder-settings');
      setSettings(res.data);
      setForm({
        reminders_enabled: res.data.reminders_enabled,
        payment_reminder_enabled: res.data.payment_reminder_enabled,
        overdue_reminder_enabled: res.data.overdue_reminder_enabled,
        payment_reminder_days_before_due: res.data.payment_reminder_days_before_due,
        overdue_reminder_days_after_due: res.data.overdue_reminder_days_after_due,
        sender_name_override: res.data.sender_name_override || '',
        reply_to_override: res.data.reply_to_override || '',
        secure_links_enabled: res.data.secure_links_enabled,
        is_active: res.data.is_active
      });
    } catch (err) {
      setError('Failed to load reminder settings');
      console.error('Failed to load reminder settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        reminders_enabled: form.reminders_enabled,
        payment_reminder_enabled: form.payment_reminder_enabled,
        overdue_reminder_enabled: form.overdue_reminder_enabled,
        payment_reminder_days_before_due: parseInt(form.payment_reminder_days_before_due),
        overdue_reminder_days_after_due: parseInt(form.overdue_reminder_days_after_due),
        sender_name_override: form.sender_name_override || null,
        reply_to_override: form.reply_to_override || null,
        secure_links_enabled: form.secure_links_enabled,
        is_active: form.is_active
      };

      const res = await api.put('/reminder-settings', payload);
      setSettings(res.data);
      
      // Reload settings from backend to confirm persistence
      await fetchSettings();
      
      toast.success('Reminder settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save reminder settings');
      console.error('Failed to save reminder settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-slate-500">Loading reminder settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-red-900">{error}</p>
          <Button onClick={fetchSettings} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Automated Reminder Settings</CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Configure automated invoice reminders. The scheduler will send reminders based on these settings for eligible invoices.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="space-y-1">
              <Label className="text-base font-medium text-slate-900">Enable Automated Reminders</Label>
              <p className="text-sm text-slate-500">
                Master switch for all automated reminder functionality
              </p>
            </div>
            <Switch
              checked={form.reminders_enabled}
              onCheckedChange={(checked) => handleChange('reminders_enabled', checked)}
              data-testid="reminders-enabled-toggle"
            />
          </div>

          {/* Payment Reminders Section */}
          <div className={`space-y-4 ${!form.reminders_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="space-y-4 p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-base font-medium text-slate-900">Payment Reminders</Label>
                  <p className="text-sm text-slate-500">
                    Send reminders before invoices are due
                  </p>
                </div>
                <Switch
                  checked={form.payment_reminder_enabled}
                  onCheckedChange={(checked) => handleChange('payment_reminder_enabled', checked)}
                  disabled={!form.reminders_enabled}
                  data-testid="payment-reminder-enabled-toggle"
                />
              </div>

              <div className={`space-y-2 ${!form.payment_reminder_enabled ? 'opacity-50' : ''}`}>
                <Label>Days Before Due Date</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={form.payment_reminder_days_before_due}
                  onChange={(e) => handleChange('payment_reminder_days_before_due', e.target.value)}
                  disabled={!form.reminders_enabled || !form.payment_reminder_enabled}
                  data-testid="payment-reminder-days-input"
                />
                <p className="text-xs text-slate-500">
                  Reminders will be sent this many days before the invoice due date (0-30 days)
                </p>
              </div>
            </div>

            {/* Overdue Reminders Section */}
            <div className="space-y-4 p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-base font-medium text-slate-900">Overdue Reminders</Label>
                  <p className="text-sm text-slate-500">
                    Send reminders after invoices become overdue
                  </p>
                </div>
                <Switch
                  checked={form.overdue_reminder_enabled}
                  onCheckedChange={(checked) => handleChange('overdue_reminder_enabled', checked)}
                  disabled={!form.reminders_enabled}
                  data-testid="overdue-reminder-enabled-toggle"
                />
              </div>

              <div className={`space-y-2 ${!form.overdue_reminder_enabled ? 'opacity-50' : ''}`}>
                <Label>Days After Due Date</Label>
                <Input
                  type="number"
                  min="0"
                  max="90"
                  value={form.overdue_reminder_days_after_due}
                  onChange={(e) => handleChange('overdue_reminder_days_after_due', e.target.value)}
                  disabled={!form.reminders_enabled || !form.overdue_reminder_enabled}
                  data-testid="overdue-reminder-days-input"
                />
                <p className="text-xs text-slate-500">
                  Reminders will be sent this many days after the invoice due date (0-90 days)
                </p>
              </div>
            </div>
          </div>

          {/* Email Customization */}
          <div className={`space-y-4 ${!form.reminders_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="space-y-1">
              <Label className="text-base font-medium text-slate-900">Email Customization (Optional)</Label>
              <p className="text-sm text-slate-500">
                Override default sender name and reply-to address for reminder emails
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sender Name Override</Label>
                <Input
                  type="text"
                  placeholder="Leave blank to use company name"
                  value={form.sender_name_override}
                  onChange={(e) => handleChange('sender_name_override', e.target.value)}
                  disabled={!form.reminders_enabled}
                  data-testid="sender-name-input"
                />
                <p className="text-xs text-slate-500">
                  Custom sender name (e.g., "Accounts Receivable")
                </p>
              </div>

              <div className="space-y-2">
                <Label>Reply-To Override</Label>
                <Input
                  type="email"
                  placeholder="Leave blank to use company email"
                  value={form.reply_to_override}
                  onChange={(e) => handleChange('reply_to_override', e.target.value)}
                  disabled={!form.reminders_enabled}
                  data-testid="reply-to-input"
                />
                <p className="text-xs text-slate-500">
                  Custom reply-to email address
                </p>
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className={`space-y-4 ${!form.reminders_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-1">
                <Label className="text-base font-medium text-slate-900">Include Secure Payment Links</Label>
                <p className="text-sm text-slate-500">
                  Include secure links in reminder emails for easy payment
                </p>
              </div>
              <Switch
                checked={form.secure_links_enabled}
                onCheckedChange={(checked) => handleChange('secure_links_enabled', checked)}
                disabled={!form.reminders_enabled}
                data-testid="secure-links-toggle"
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-reminder-settings"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="border border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Bell className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2 text-sm text-blue-900">
              <p className="font-medium">How Automated Reminders Work:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Reminders are sent automatically by the system scheduler</li>
                <li>Only eligible invoices are considered (sent status, balance due &gt; 0)</li>
                <li>Each invoice receives only one reminder per configured window</li>
                <li>Paid, draft, and void invoices are never sent reminders</li>
                <li>You can customize email templates in the Email Templates tab</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Super Admin Platform Subscription Manager (Admin > Subscription) ───
function SuperAdminSubscriptionPanel() {
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (planFilter !== 'all') params.plan = planFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [overviewRes, planRes] = await Promise.all([
        api.get('/admin/subscriptions/overview', { params }),
        api.get('/pricing').catch(() => ({ data: [] })),
      ]);
      setOverview(overviewRes.data);
      setPlans(planRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
    }
  }, [search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => {
    const matchedPlan = plans.find((p) => p.slug === row.plan_slug);
    setEditing({
      account_id: row.account_id,
      company_name: row.company_name,
      plan_id: row.plan_id || matchedPlan?.id || '',
      status: row.status || 'active',
      subscription_source: row.subscription_source || row.billing_source || 'manual',
      stripe_customer_id: row.stripe_customer_id || '',
      stripe_subscription_id: row.stripe_subscription_id || '',
    });
    setShowDialog(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/subscriptions/${editing.account_id}`, {
        plan_id: editing.plan_id || null,
        status: editing.status,
        subscription_source: editing.subscription_source,
        stripe_customer_id: editing.stripe_customer_id || null,
        stripe_subscription_id: editing.stripe_subscription_id || null,
      });
      toast.success('Subscription updated');
      setShowDialog(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Platform Subscription Management</CardTitle>
          <p className="text-sm text-slate-500">
            Super Admin overview of all accounts, subscription state, and manual overrides.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Total</p><p className="text-lg font-semibold">{overview.total_signed_up_accounts}</p></div>
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Free</p><p className="text-lg font-semibold">{overview.free_accounts}</p></div>
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Pro</p><p className="text-lg font-semibold">{overview.pro_accounts}</p></div>
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Business</p><p className="text-lg font-semibold">{overview.business_accounts}</p></div>
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Active</p><p className="text-lg font-semibold">{overview.active_count}</p></div>
              <div className="rounded border p-3"><p className="text-xs text-slate-500">Canceled / Past Due</p><p className="text-lg font-semibold">{(overview.cancelled_count || 0) + (overview.past_due_count || 0)}</p></div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Search company or email" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger><SelectValue placeholder="Plan filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="business">Business</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Status filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load}>Apply Filters</Button>
          </div>

          {!overview ? (
            <p className="text-sm text-slate-500">Loading subscriptions...</p>
          ) : overview.rows.length === 0 ? (
            <p className="text-sm text-slate-500">No subscriptions found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Owner Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subscription Source</TableHead>
                  <TableHead>Stripe Customer</TableHead>
                  <TableHead>Stripe Subscription</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.rows.map((row) => (
                  <TableRow key={row.account_id}>
                    <TableCell className="font-medium">{row.company_name}</TableCell>
                    <TableCell>{row.primary_user_email || row.company_email || '-'}</TableCell>
                    <TableCell>{row.plan_display_name}</TableCell>
                    <TableCell><Badge>{row.status}</Badge></TableCell>
                    <TableCell>{row.subscription_source}</TableCell>
                    <TableCell className="text-xs">{row.stripe_customer_id || '-'}</TableCell>
                    <TableCell className="text-xs">{row.stripe_subscription_id || '-'}</TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Subscription - {editing?.company_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {editing.subscription_source === 'stripe' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Stripe-backed subscription. Edits here are manual overrides.
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan ID</Label>
                  <Select value={editing.plan_id || ''} onValueChange={(v) => setEditing({ ...editing, plan_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} ({plan.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trialing">Trialing</SelectItem>
                      <SelectItem value="past_due">Past Due</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Subscription Source</Label>
                <Select value={editing.subscription_source} onValueChange={(v) => setEditing({ ...editing, subscription_source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">manual</SelectItem>
                    <SelectItem value="stripe">stripe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stripe Customer ID</Label>
                  <Input value={editing.stripe_customer_id} onChange={(e) => setEditing({ ...editing, stripe_customer_id: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Stripe Subscription ID</Label>
                  <Input value={editing.stripe_subscription_id} onChange={(e) => setEditing({ ...editing, stripe_subscription_id: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Subscription Panel ───
function SubscriptionPanel() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchData = useCallback(() => {
    Promise.all([
      api.get('/subscription').catch(err => { setLoadError('Failed to load subscription'); return null; }),
      api.get('/pricing').catch(() => ({ data: [] })),
    ]).then(([subRes, planRes]) => {
      if (subRes) setSub(subRes.data);
      if (planRes) setPlans(planRes.data);
    });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      toast.success('Subscription activated! Your plan has been updated.');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(fetchData, 1500);
    } else if (params.get('checkout') === 'cancelled') {
      toast.info('Checkout was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchData]);

  const handleSelectPlan = async (plan) => {
    const isPaid = parseFloat(plan.monthly_price) > 0;
    setSaving(true);

    try {
      if (isPaid) {
        const res = await api.post('/stripe/checkout', { plan_slug: plan.slug });
        if (res.data.url) {
          window.location.href = res.data.url;
          return;
        }
      } else {
        const res = await api.put('/subscription', { plan: plan.slug });
        setSub(res.data);
        toast.success(`Switched to ${plan.name} plan`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to change plan';
      if (err.response?.status === 503) {
        toast.error('Stripe is not configured yet. Contact support to enable paid plans.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await api.post('/stripe/portal');
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open billing portal');
    }
  };

  if (!sub && !loadError) return <div className="p-4 text-slate-500">Loading subscription...</div>;
  if (loadError) return <div className="p-4 text-red-500">{loadError}</div>;

  const currentSlug = sub?.plan_name_snapshot?.toLowerCase() || sub?.plan || 'free';
  const hasStripe = !!sub?.stripe_subscription_id;
  const statusColor = sub?.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
    sub?.status === 'past_due' ? 'bg-amber-100 text-amber-700' :
    sub?.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600';

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Current Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Plan:</span>
            <span className="text-sm font-semibold text-slate-900 capitalize">{sub.plan_name_snapshot || sub.plan}</span>
            <Badge className={`text-xs ${statusColor}`}>{sub.status}</Badge>
          </div>
          {sub.price_snapshot > 0 && (
            <p className="text-sm text-slate-500">Price: <span className="font-medium text-slate-900">${parseFloat(sub.price_snapshot).toFixed(0)}/mo</span></p>
          )}
          {sub.current_period_end && (
            <p className="text-sm text-slate-500">Current period ends: <span className="font-medium text-slate-900">{new Date(sub.current_period_end).toLocaleDateString()}</span></p>
          )}
          {hasStripe && (
            <Button variant="outline" size="sm" onClick={handleManageBilling} className="mt-2">
              Manage Billing & Invoices
            </Button>
          )}
        </CardContent>
      </Card>

      {plans.length === 0 ? (
        <p className="text-sm text-slate-500">No plans available.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map(p => {
            const isCurrent = currentSlug === p.slug || sub.plan_name_snapshot === p.name;
            const isPaid = parseFloat(p.monthly_price) > 0;
            return (
              <Card key={p.id} className={`border shadow-sm ${isCurrent ? 'border-blue-600 ring-2 ring-blue-100' : p.is_recommended ? 'border-blue-300' : 'border-slate-200'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
                    {p.is_recommended && <Badge className="bg-blue-100 text-blue-700 text-xs">Recommended</Badge>}
                  </div>
                  <p className="text-2xl font-bold text-blue-600 mt-2">
                    {isPaid ? `$${parseFloat(p.monthly_price).toFixed(0)}` : 'Free'}
                    {isPaid && <span className="text-sm text-slate-400">/mo</span>}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                  <ul className="mt-4 space-y-1.5">
                    {p.bullet_points.map((bp, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2"><span className="text-emerald-500 mt-0.5">&#10003;</span>{bp}</li>)}
                  </ul>
                  <Button
                    className={`w-full mt-4 ${isCurrent ? 'bg-slate-100 text-slate-500 hover:bg-slate-100' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                    disabled={isCurrent || saving}
                    onClick={() => handleSelectPlan(p)}
                    data-testid={`plan-${p.slug}`}
                  >
                    {isCurrent ? 'Current Plan' : isPaid && !hasStripe ? `Subscribe to ${p.name}` : p.cta_text}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Email Templates Panel ───
function EmailTemplatesPanel({ scope = 'account' }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Default templates from backend (for fallback reference)
  const DEFAULT_TEMPLATES = {
    quote_sent: {
      subject: "Quote {{document_number}} from {{company_name}}",
      body: `Hello {{client_name}},

Please find attached Quote {{document_number}} for {{project_name}}.

Total (incl. taxes): {{amount}}

You can view and respond to this quote online:
{{secure_link}}

If you have any questions, please don't hesitate to reach out.

Best regards,
{{company_name}}`
    },
    estimate_sent: {
      subject: "Estimate {{document_number}} from {{company_name}}",
      body: `Hello {{client_name}},

Please find attached Estimate {{document_number}} for {{project_name}}.

Total (incl. taxes): {{amount}}

You can view and respond to this estimate online:
{{secure_link}}

If you have any questions, please don't hesitate to reach out.

Best regards,
{{company_name}}`
    },
    invoice_sent: {
      subject: "Invoice {{document_number}} from {{company_name}}",
      body: `Hello {{client_name}},

Please find attached Invoice {{document_number}} for {{project_name}}.

Total (incl. taxes): {{amount}}
Due Date: {{due_date}}

You can view this invoice online:
{{secure_link}}

Thank you for your business.

Best regards,
{{company_name}}`
    },
    payment_reminder: {
      subject: "Payment Reminder - Invoice {{document_number}}",
      body: `Hello {{client_name}},

This is a friendly reminder that Invoice {{document_number}} for {{project_name}} is due on {{due_date}}.

Invoice Total: {{amount}}

You can view and pay this invoice online:
{{secure_link}}

If you have already made payment, please disregard this reminder.

Best regards,
{{company_name}}`
    },
    overdue_reminder: {
      subject: "Overdue Invoice {{document_number}} - Action Required",
      body: `Hello {{client_name}},

Invoice {{document_number}} for {{project_name}} is now overdue. The due date was {{due_date}}.

Invoice Total: {{amount}}

Please arrange payment at your earliest convenience. You can view the invoice online:
{{secure_link}}

If you have any questions or concerns, please contact us immediately.

Best regards,
{{company_name}}`
    },
    verification_email: {
      subject: "Verify your ServiceFlow account",
      body: `
Hello {{first_name}},

Welcome to ServiceFlow!

Please verify your email address by clicking the link below:
{{verification_link}}

This link will expire in 24 hours.

If you didn't create a ServiceFlow account, please ignore this email.

Best regards,
The ServiceFlow Team`
    }
  };

  const TEMPLATE_KEYS = [
    { key: 'quote_sent', label: 'Quote Sent' },
    { key: 'estimate_sent', label: 'Estimate Sent' },
    { key: 'invoice_sent', label: 'Invoice Sent' },
    { key: 'payment_reminder', label: 'Payment Reminder' },
    { key: 'overdue_reminder', label: 'Overdue Reminder' },
    { key: 'verification_email', label: 'Verification Email' }
  ];

  const PLACEHOLDERS = [
    { key: '{{client_name}}', desc: 'Client full name' },
    { key: '{{company_name}}', desc: 'Your company name' },
    { key: '{{company_logo}}', desc: 'Company logo rendered as <img> tag (falls back to company name)' },
    { key: '{{company_logo_url}}', desc: 'Raw logo image URL (for use in custom HTML src="...")' },
    { key: '{{document_number}}', desc: 'Quote/Estimate/Invoice number' },
    { key: '{{project_name}}', desc: 'Project name' },
    { key: '{{amount}}', desc: 'Document total amount' },
    { key: '{{due_date}}', desc: 'Invoice due date' },
    { key: '{{po_number}}', desc: 'Client PO number' },
    { key: '{{ticket_number}}', desc: 'Client ticket number' },
    { key: '{{secure_link}}', desc: 'Public document view link' },
    { key: '{{verification_link}}', desc: 'Email verification link (verification_email only)' },
    { key: '{{first_name}}', desc: 'User first name (verification_email only)' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, [scope]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/email-templates', { params: { scope } });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const getTemplateForKey = (key) => {
    return templates.find(t => t.template_key === key);
  };

  const getEffectiveTemplate = (key) => {
    const accountTemplate = getTemplateForKey(key);
    if (accountTemplate && accountTemplate.is_active) {
      return {
        source: 'account',
        subject: accountTemplate.subject_template,
        body: accountTemplate.body_template
      };
    }
    return {
      source: 'default',
      subject: DEFAULT_TEMPLATES[key]?.subject || '',
      body: DEFAULT_TEMPLATES[key]?.body || ''
    };
  };

  const handleEdit = (key) => {
    const accountTemplate = getTemplateForKey(key);
    const defaultTemplate = DEFAULT_TEMPLATES[key];
    
    if (accountTemplate) {
      setEditingTemplate(accountTemplate);
    } else {
      // Create new template based on defaults
      setEditingTemplate({
        template_key: key,
        template_name: TEMPLATE_KEYS.find(tk => tk.key === key)?.label || key,
        subject_template: defaultTemplate?.subject || '',
        body_template: defaultTemplate?.body || '',
        is_active: true,
        isNew: true
      });
    }
    setShowEditDialog(true);
  };

  const handleSave = async (templateData) => {
    try {
      const hasPersistedId = !!templateData.id && String(templateData.id) !== 'undefined';
      if (templateData.isNew || !hasPersistedId) {
        const { isNew, ...createData } = templateData;
        await api.post('/email-templates', createData, { params: { scope } });
        toast.success('Email template created successfully');
      } else {
        const { id, isNew, created_at, updated_at, account_id, ...updateData } = templateData;
        await api.put(`/email-templates/${id}`, updateData, { params: { scope } });
        toast.success('Email template updated successfully');
      }
      await fetchTemplates();
      setShowEditDialog(false);
      setEditingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email template');
    }
  };

  if (loading) {
    return <div className="p-4 text-slate-500">Loading email templates...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Email Templates</CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {scope === 'global'
              ? 'These are platform starter templates. New subscribers use them until they customize their own.'
              : 'Customize the email messages sent to your clients. If not customized, platform defaults will be used.'}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {TEMPLATE_KEYS.map(({ key, label }) => {
                const accountTemplate = getTemplateForKey(key);
                const effective = getEffectiveTemplate(key);
                const status = !accountTemplate 
                  ? 'default' 
                  : accountTemplate.is_active 
                    ? 'active' 
                    : 'inactive';
                
                return (
                  <TableRow key={key} data-testid={`email-template-row-${key}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-slate-900">{label}</div>
                        <div className="text-xs text-slate-500">{key}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {status === 'default' && (
                        <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                          Using Default
                        </Badge>
                      )}
                      {status === 'active' && (
                        <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                          Customized
                        </Badge>
                      )}
                      {status === 'inactive' && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">
                          Inactive (Using Default)
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {accountTemplate?.updated_at 
                        ? formatDate(accountTemplate.updated_at)
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(key)}
                        data-testid={`edit-template-${key}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        {accountTemplate ? 'Edit' : 'Customize'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {showEditDialog && editingTemplate && (
        <EditTemplateDialog
          template={editingTemplate}
          defaultTemplate={DEFAULT_TEMPLATES[editingTemplate.template_key]}
          placeholders={PLACEHOLDERS}
          onSave={handleSave}
          onClose={() => {
            setShowEditDialog(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Edit Template Dialog ───
function EditTemplateDialog({ template, defaultTemplate, placeholders, onSave, onClose }) {
  const [formData, setFormData] = useState({
    id: template.id,
    account_id: template.account_id,
    created_at: template.created_at,
    updated_at: template.updated_at,
    template_key: template.template_key,
    template_name: template.template_name,
    subject_template: template.subject_template,
    body_template: template.body_template,
    is_active: template.is_active,
    isNew: template.isNew
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  // Example data for preview — uses generic labels so users don't confuse preview with real output
  const EXAMPLE_DATA = {
    client_name: 'Jane Smith',
    company_name: '[Your Company Name]',
    company_logo: '<span style="font-size:20px;font-weight:600;color:#1e40af;">[Your Logo]</span>',
    company_logo_url: '',
    document_number: 'QT-1001',
    project_name: 'Sample Project',
    amount: '$2,500.00',
    due_date: '2026-04-15',
    po_number: 'PO-12345',
    ticket_number: 'TK-67890',
    secure_link: 'https://serviceflowhq.app/view/abc123...',
    verification_link: 'https://serviceflowhq.app/verify-email/xyz789...',
    first_name: 'Jane'
  };

  const renderPreview = (text) => {
    let result = text;
    Object.keys(EXAMPLE_DATA).forEach(key => {
      const placeholder = `{{${key}}}`;
      result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), EXAMPLE_DATA[key]);
    });
    return result;
  };

  const copyPlaceholder = (placeholder) => {
    navigator.clipboard.writeText(placeholder);
    toast.success(`Copied ${placeholder}`);
  };

  const handleResetToDefault = () => {
    setFormData(prev => ({
      ...prev,
      subject_template: defaultTemplate?.subject || '',
      body_template: defaultTemplate?.body || ''
    }));
    toast.success('Reset to default template');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Edit Email Template: {formData.template_name}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Template Key: <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{formData.template_key}</code>
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Active Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <Label className="font-medium text-slate-900">Active</Label>
              <p className="text-xs text-slate-500 mt-1">
                When inactive, system default template will be used
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              data-testid="template-active-toggle"
            />
          </div>

          {/* Subject Template */}
          <div className="space-y-2">
            <Label htmlFor="subject_template" className="text-sm font-medium text-slate-900">
              Subject Template
            </Label>
            <Input
              id="subject_template"
              value={formData.subject_template}
              onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e.target.value }))}
              placeholder="Email subject..."
              className="font-mono text-sm"
              data-testid="subject-template-input"
            />
          </div>

          {/* Body Template */}
          <div className="space-y-2">
            <Label htmlFor="body_template" className="text-sm font-medium text-slate-900">
              Body Template
            </Label>
            <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-900">
                <strong>HTML Supported:</strong> You can use HTML tags to style your email. 
                Placeholders like <code className="bg-blue-100 px-1 rounded">{'{{client_name}}'}</code> work in HTML templates.
              </p>
            </div>
            <Textarea
              id="body_template"
              value={formData.body_template}
              onChange={(e) => setFormData(prev => ({ ...prev, body_template: e.target.value }))}
              placeholder="Email body (HTML supported)..."
              rows={12}
              className="font-mono text-sm"
              data-testid="body-template-input"
            />
          </div>

          {/* Placeholders Helper */}
          <div className="border border-slate-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowPlaceholders(!showPlaceholders)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <span className="text-sm font-medium text-slate-900">Available Placeholders</span>
              <span className="text-xs text-slate-500">{showPlaceholders ? 'Hide' : 'Show'}</span>
            </button>
            {showPlaceholders && (
              <div className="px-4 pb-4 space-y-2 border-t border-slate-200 pt-3">
                {placeholders.map(({ key, desc }) => (
                  <div key={key} className="flex items-center justify-between group">
                    <div className="flex-1">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-900">{key}</code>
                      <span className="text-xs text-slate-500 ml-2">{desc}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyPlaceholder(key)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="border border-slate-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <span className="text-sm font-medium text-slate-900">Preview with Example Data</span>
              <span className="text-xs text-slate-500">{showPreview ? 'Hide' : 'Show'}</span>
            </button>
            {showPreview && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-200 pt-3">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Subject:</Label>
                  <div className="mt-1 p-3 bg-slate-50 rounded border border-slate-200 text-sm">
                    {renderPreview(formData.subject_template)}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Body Preview (HTML Rendered):</Label>
                  <div 
                    className="mt-1 p-3 bg-white rounded border border-slate-200 text-sm"
                    dangerouslySetInnerHTML={{ __html: renderPreview(formData.body_template) }}
                    style={{ maxHeight: '400px', overflow: 'auto' }}
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    Note: This is a simplified preview. The actual email will be wrapped in the full branded layout with your company logo and footer.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Default Template Reference */}
          {defaultTemplate && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">System Default Template</p>
                  <p className="text-xs text-blue-700 mt-1">
                    This is the fallback template used when no custom template is active
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResetToDefault}
                  className="ml-4"
                >
                  Reset to Default
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <DialogFooter className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-template-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ─── Audit Log Panel ───
function AuditLogPanel() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { 
    api.get('/audit-logs?limit=50').then(r => setLogs(r.data)).catch(err => {
      console.error('Failed to load audit logs:', err);
      setLogs([]);
    });
  }, []);
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader><CardTitle className="text-lg font-medium">Audit Log</CardTitle></CardHeader>
      <CardContent className="p-0">
        {logs.length === 0 ? <div className="p-8 text-center text-slate-500">No entries</div> : (
          <Table><TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
            <TableBody>{logs.map(log => (<TableRow key={log.id}><TableCell className="text-sm">{new Date(log.created_at).toLocaleString('en-CA')}</TableCell><TableCell><Badge variant="outline" className="text-xs">{log.action}</Badge></TableCell><TableCell className="text-sm">{log.entity_type}</TableCell><TableCell className="text-xs text-slate-500 max-w-xs truncate">{log.details ? JSON.stringify(log.details) : '-'}</TableCell></TableRow>))}</TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
